<template>
  <UiModal
    :label="t('modals.deposit.title')"
    @close="emits('close')"
  >
    <form
      @submit="onSubmit"
      :class="$style.form"
    >
      <UiSelect
        :label="t('modals.deposit.method')"
        :placeholder="t('modals.deposit.method')"
        name="method"
        :options="paymentOptions"
      />

      <UiAmountField
        :label="t('modals.deposit.amount')"
        name="amount"
        :amount="amount"
      />

      <UiButton
        wide
        :loading="loader"
      >
        <template #label>
          {{ t('modals.deposit.button') }}
        </template>
      </UiButton>
    </form>
  </UiModal>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import type { SelectOption } from '@/components/ui/Select';
import type { PaymentBalanceParams } from './index.js';

const { t } = useI18n();

const emits = defineEmits(['close']);
defineProps<PaymentBalanceParams>();

const { $mixpanel } = useNuxtApp();

export interface PaymentProvider {
  deposit: boolean;
  subscription: boolean;
  paymentIntervals: number[];
}

export interface ThirdParties {
  stripe: PaymentProvider;
  zeroXProcessing: PaymentProvider;
  ngenius: PaymentProvider;
  nowpayments: PaymentProvider;
}

const paymentMethods = ref<ThirdParties>();

const STRIPE_DEPOSIT_MIN = 20;
const STRIPE_DEPOSIT_MAX = 50;

const paymentOptions = computed<SelectOption[]>(() => {
  const response: SelectOption[] = [];

  if (paymentMethods.value?.zeroXProcessing.deposit) {
    response.push({
      label: t('modals.subscription.payment.xProcessing'),
      value: 'zeroXProcessing',
      icon: '/images/crypto.svg',
    });
  }

  if (paymentMethods.value?.stripe.deposit) {
    response.push({
      label: t('modals.subscription.payment.card'),
      value: 'stripe',
      icon: '/images/card.svg',
    });
  }

  if (paymentMethods.value?.nowpayments.deposit) {
    response.push({
      label: t('modals.subscription.payment.nowpayments'),
      value: 'nowpayments',
      icon: '/images/card.svg',
    });
  }

  if (paymentMethods.value?.ngenius.deposit) {
    response.push({
      label: t('modals.subscription.payment.ngenius'),
      value: 'ngenius',
      icon: '/images/card.svg',
    });
  }

  return response;
});

const toastStore = useToastStore();

const schema: yup.AnyObject = yup.object({
  method: yup.string().required(t('errors.paymentMethod')),
  amount: yup
    .number()
    .required()
    .when('method', {
      is: 'stripe',
      then: (amountSchema) => amountSchema
        .min(STRIPE_DEPOSIT_MIN, `Minimum deposit amount is $${STRIPE_DEPOSIT_MIN}`)
        .max(STRIPE_DEPOSIT_MAX, `Maximum deposit amount is $${STRIPE_DEPOSIT_MAX}`),
      otherwise: (amountSchema) => amountSchema.min(0.01),
    }),
});

const loader = ref<boolean>(false);
const { handleSubmit } = useForm({
  validationSchema: schema,
});

const paymentByCard = async (amount: number): Promise<string> => {
  try {
    const paymentApi = useNuxtApp().$paymentApi;

    const { result, data } = await paymentApi.cardDeposit(amount);

    if (result) {
      return data.url;
    } else throw new Error('Error @cardDeposit');
  } catch (error) {
    console.error(error);

    return '';
  }
};

const paymentByCrypto = async (amount: number): Promise<string> => {
  try {
    const paymentApi = useNuxtApp().$paymentApi;

    const { result, data } = await paymentApi.cryptoDeposit(amount);

    if (result) {
      return data.paymentUrl;
    } else throw new Error('Error @cardDeposit');
  } catch (error) {
    console.error(error);

    return '';
  }
};



const paymentByNgenius = async (amount: number): Promise<string>  => {
  try {
    const paymentApi = useNuxtApp().$paymentApi;

    const { result, data } = await paymentApi.ngeniusDeposit(amount);

    if (result) {
      return data.paymentUrl;
    } else throw new Error('Error @cardDeposit');
  } catch (error) {
    console.error(error);

    return '';
  }
}

const paymentByNowPayments = async (amount: number): Promise<string> => {
  try {
    const paymentApi = useNuxtApp().$paymentApi;

    const { result, data } = await paymentApi.cryptoDepositNowpayments(amount);

    if (result) {
      return data.paymentUrl;
    } else throw new Error('Error @cardDeposit');
  } catch (error) {
    console.error(error);

    return '';
  }
};

const onSubmit = handleSubmit(
  async (values): Promise<void> => {
    try {
      loader.value = true;

      const paymentHandlers: Record<string, (amount: number) => Promise<string>> = {
        zeroXProcessing: paymentByCrypto,
        stripe: paymentByCard,
        ngenius: paymentByNgenius,
        nowpayments: paymentByNowPayments,
      };

      const handler = paymentHandlers[values.method];

      if (!handler) {
        throw new Error(`Unsupported payment method: ${values.method}`);
      }
      if (values.method === 'stripe' && (values.amount < STRIPE_DEPOSIT_MIN || values.amount > STRIPE_DEPOSIT_MAX)) {
        throw new Error(`Stripe deposit amount must be between $${STRIPE_DEPOSIT_MIN} and $${STRIPE_DEPOSIT_MAX}`);
      }
      if (values.method === 'nowpayments' && values.amount < 20) {
        throw new Error(`Minimum deposit amount is $20`);
      }

      const url = await handler(values.amount);

      if (!url) {
        return;
      }

      window.open(url, '_blank');
      emits('close');
    } catch (error) {
      console.error(error);
      toastStore.showDanger({
        text: `The payment could not be generated: ${error.message}`,
        duration: 2000,
      });
    } finally {
      loader.value = false;
    }
  }
);

const getMethods = async () => {
  try {
    const authApi = useNuxtApp().$authApi;

    const { result, data } = await authApi.getPaymentMethods();

    if (result) {
      paymentMethods.value = data?.paymentThirdParties;
    }
  } catch (err) {
    console.error(err);
  }
};

onMounted(async () => {
  await getMethods();
});
</script>

<style lang="scss" module>
  .form {

    > * {
      width: 100%;
    }
  }
</style>